/**
 * Recap notes (demo #9) — a project-scoped "what happened on site" log that the
 * crew builds up over the course of a job (photos + a line of context) and the
 * supervisor reviews, AI-polishes, and ships in the closeout recap.
 *
 * Purely INTERNAL capture: read only by the supervisor + the recap builder on
 * the closeout screen. No @mentions and no notifications — unlike crew chat or
 * the requester-facing "Updates" feed.
 */
import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';
/** A photo/file pinned to a recap note. References an `attachments` row. */
/**
 * Sentinel body for a photos-only recap note (composer requires a body). Shared
 * so the web composer, the note renderer, and the AI-draft filter can never
 * drift on the magic string (audit 2026-08-27).
 */
export const RECAP_PHOTOS_ONLY_BODY = '(photos)';
export const RecapNoteAttachmentSchema = z.object({
    id: UuidSchema,
    filename: z.string(),
    content_type: z.string().nullable(),
    /** Signed, expiring download URL — what the <img>/<a> uses. */
    file_url: z.string().nullable(),
    kind: z.string().nullable(),
    /** True when the content type is an image — render as a thumbnail, not a chip. */
    is_image: z.boolean(),
});
/** One recap note, joined to its author + resolved mentions for rendering. */
export const RecapNoteSchema = z.object({
    id: UuidSchema,
    project_id: UuidSchema,
    author_user_id: UuidSchema.nullable(),
    author_display_name: z.string(),
    /** The crew's raw note as typed. */
    body: z.string(),
    /** AI-polished / supervisor-edited copy used in the recap; null until polished. */
    body_polished: z.string().nullable(),
    source: z.enum(['text', 'voice']),
    attachments: z.array(RecapNoteAttachmentSchema),
    created_at: IsoDateTimeSchema,
});
/** POST body — raw text + optional attachment ids (validated server-side). */
export const RecapNoteCreateSchema = z.object({
    body: z.string().min(1).max(10_000),
    source: z.enum(['text', 'voice']).optional().default('text'),
    attachment_ids: z.array(UuidSchema).max(20).optional(),
});
/** PATCH body — the supervisor edits the polished copy (or the raw body) at closeout. */
export const RecapNoteUpdateSchema = z
    .object({
    body: z.string().min(1).max(10_000).optional(),
    body_polished: z.string().max(10_000).nullable().optional(),
})
    .refine((v) => v.body !== undefined || v.body_polished !== undefined, {
    message: 'Provide body or body_polished to update.',
});
/** Response from the AI-polish endpoint — a DRAFT for the editor (never auto-written). */
export const RecapNotePolishResponseSchema = z.object({
    polished: z.string(),
    source: z.enum(['stub', 'gemini']),
});
export const RecapNoteListResponseSchema = z.object({
    /** Oldest-first so the log reads top-to-bottom in capture order. */
    data: z.array(RecapNoteSchema),
});
//# sourceMappingURL=recap-notes.js.map